import { Box } from '@tanium/react-box';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { Shimmer } from '@tanium/react-shimmer';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AsyncStatus } from '../../hooks/useAsync';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { IdpResponse } from '../../models/IdpModels';
import { MediumBoldText, MediumText } from '../../TextStyles';
import { AsyncFailure } from '../AsyncSuspense';

interface DeleteIdpModalProps {
  idp?: IdpResponse;
  isOpen: boolean;
  onRequestClose: () => void;
}

export const DeleteIdpModal: React.VFC<DeleteIdpModalProps> = ({ idp, isOpen, onRequestClose }) => {
  const { t } = useTranslation();
  const { loadIdps, deleteIdp: deleteProvider } = useIdentityProviderConfigs();

  useEffect(() => {
    if (deleteProvider.status === AsyncStatus.Success) {
      onRequestClose();
    }
  });

  const onConfirm = async () => {
    await deleteProvider.execute(idp!.id);
    await loadIdps.execute();
  };

  const onCancel = () => {
    deleteProvider.reset();
    onRequestClose();
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      header={t('Delete Identity Provider')}
      size="md"
      confirmLabel={t('Delete')}
      onCancel={onCancel}
      onConfirm={onConfirm}
      isConfirmDisabled={deleteProvider.status === AsyncStatus.Pending}
      onRequestClose={deleteProvider.reset}
    >
      <MediumText>{t('Are you sure you want to delete this identity provider?')}</MediumText>
      <p>
        <MediumBoldText>{idp?.displayName}</MediumBoldText>
      </p>
      {deleteProvider.status === AsyncStatus.Pending && (
        <Box mt={2} height={1}>
          <Shimmer />
        </Box>
      )}
      {deleteProvider.status === AsyncStatus.Failure && (
        <Box pb={1}>
          <AsyncFailure error={deleteProvider.error} />
        </Box>
      )}
    </ConfirmModal>
  );
};
